import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PercentCrop, PixelCrop, ReactCrop } from 'react-image-crop'

import 'react-image-crop/src/ReactCrop.scss'
import FormButtons from '../FormButtons/FormButtons'
import Modal from '../Modal/Modal'
import styles from './CropImageDialog.module.scss'

/**********************************************************************
 * cropImage
 **********************************************************************/
export function cropImage(image: HTMLImageElement, crop: PercentCrop | undefined) {
  if (!crop) {
    return image.src
  }

  const cropX = Math.max(0, Math.min(image.naturalWidth - 1, Math.round((image.naturalWidth * crop.x) / 100)))
  const cropY = Math.max(0, Math.min(image.naturalHeight - 1, Math.round((image.naturalHeight * crop.y) / 100)))
  const croppedWidth = Math.round((image.naturalWidth * crop.width) / 100)
  const croppedHeight = Math.round((image.naturalHeight * crop.height) / 100)

  const canvas = document.createElement('canvas')
  canvas.width = croppedWidth
  canvas.height = croppedHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return image.src
  }
  ctx.drawImage(image, cropX, cropY, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight)
  return canvas.toDataURL()
}

/**********************************************************************
 * CropImageDialog
 **********************************************************************/
type CropImageDialogProps = {
  source: string
  crop?: PercentCrop

  aspect?: number
  circularCrop?: boolean

  onClose: () => void
  onSubmit: ({ crop, imageUrl }: { crop: PercentCrop | undefined; imageUrl: string }) => void
}

export default React.memo(function CropImageDialog({
  source,
  crop: initialCrop,
  aspect,
  circularCrop,
  onClose,
  onSubmit,
}: CropImageDialogProps) {
  const { t } = useTranslation()

  const imageRef = useRef<HTMLImageElement | null>(null)
  const [crop, setCrop] = useState<PercentCrop | undefined>(initialCrop)

  const handleChange = useCallback(
    (_: PixelCrop, crop: PercentCrop) => {
      setCrop(crop)
    },
    [setCrop]
  )

  const handleSubmit = useCallback(() => {
    const image = imageRef.current
    if (!image) {
      return onSubmit({ crop, imageUrl: source })
    }
    onSubmit({ crop, imageUrl: cropImage(image, crop) })
  }, [onSubmit, crop, source])

  return (
    <Modal onClose={onClose} className={styles.CropImageDialog}>
      <header>
        <h1>{t('crop image title')}</h1>
      </header>
      <div>
        <ReactCrop
          aspect={aspect}
          circularCrop={circularCrop}
          crop={crop}
          onChange={handleChange}
          className={styles.crop}
        >
          <img ref={imageRef} src={source} className={styles.image} />
        </ReactCrop>
      </div>
      <FormButtons onClose={onClose} onSubmit={handleSubmit} />
    </Modal>
  )
})
